// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickableLinkProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const getCategoriesParameter = {
        maxDepth: 1,
        includeStartCategory: false,
    };
    const catalogSettings = getSettingsCollection(state).catalogSettings as any;
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        notFoundImagePath: catalogSettings?.notFoundMediumImagePath,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryGalleryStyles {
    wrapper?: InjectableCss;
    categoryWrapper?: ClickableLinkProps;
    imageCategoryWrapper?: InjectableCss;
    categoryImage?: LazyImageProps;
    categoryName?: TypographyProps;
}

export const categoryGalleryStyles: CategoryGalleryStyles = {};

const styles = categoryGalleryStyles;

const CategoryGallery: FC<Props> = ({
    categoriesDataView,
    getCategoriesParameter,
    notFoundImagePath,
    loadCategories,
}) => {
    React.useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }, []);

    const categories = categoriesDataView.value;

    if (!categories) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {categories.map(category => (
                <Clickable key={category.id} href={category.path} {...styles.categoryWrapper}>
                    <StyledWrapper {...styles.imageCategoryWrapper}>
                        <LazyImage
                            src={category.smallImagePath || notFoundImagePath}
                            altText={`Image Of ${category.imageAltText}`}
                            {...styles.categoryImage}
                        />
                    </StyledWrapper>
                    <Typography {...styles.categoryName}>{category.shortDescription}</Typography>
                </Clickable>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CategoryGallery)),
    definition: {
        group: "Categories",
        icon: "image",
        allowedContexts: ["CategoryListPage", "HomePage"],
    },
};

export default widgetModule;
